import React from 'react';
import './Onboarding.css';
import GuidedTourOverlay from './guidedTour/GuidedTourOverlay';
import type { GuidedTourStep } from './guidedTour/types';
import { useGuidedTour } from '../hooks/useGuidedTour';

interface OnboardingProps {
  isOpen: boolean;
  onClose: () => void;
}

const STEPS: GuidedTourStep[] = [
  {
    id: 'hero-title',
    title: 'Добро пожаловать в intellemma AI Tuner',
    description:
      'This is your AI model optimization platform. Configure and fine-tune your assistant for better performance on real evaluation data.',
    position: 'right',
    offset: { x: 30, y: 0 },
  },
  {
    id: 'optimization-type',
    title: 'Выберите тип оптимизации',
    description:
      'Select standard optimization or Telegram integration. Telegram mode optimizes the assistant connected through the Integration tab.',
    position: 'left',
    offset: { x: -30, y: 0 },
  },
  {
    id: 'config-section',
    title: 'Панель конфигурации',
    description:
      'Set model parameters here — temperature, dataset, and other controls that shape how your AI responds.',
    position: 'right',
    offset: { x: 30, y: 0 },
  },
  {
    id: 'visualization',
    title: 'Визуализация конфигурации',
    description:
      'A visual map of your current configuration. Colored regions show how parameters influence assistant behavior.',
    position: 'left',
    offset: { x: -30, y: 0 },
  },
  {
    id: 'slider-section',
    title: 'Ползунок итераций',
    description:
      'Compare optimization iterations and pick the configuration you prefer. Each step reflects a tuning pass.',
    position: 'left',
    offset: { x: -30, y: 0 },
  },
  {
    id: 'evaluation-section',
    title: 'Метрики оценки',
    description:
      'Track performance over time. Evaluation scores and embedding plots show how the configuration evolves across iterations.',
    position: 'right',
    offset: { x: 30, y: 0 },
  },
  {
    id: 'chat-comparison',
    title: 'Сравнение чатов',
    description:
      'Test the assistant in real conversations. Compare answers between iterations to see qualitative improvements.',
    position: 'right',
    offset: { x: 30, y: 0 },
  },
  {
    id: 'sidebar',
    title: 'Боковая навигация',
    description:
      'Move between product areas — Connector, Multi-Agent, Profile, and more. Integration connects your AI to Telegram.',
    position: 'right',
    offset: { x: 30, y: 0 },
  },
];

const Onboarding = ({ isOpen, onClose }: OnboardingProps) => {
  const tour = useGuidedTour({
    isOpen,
    steps: STEPS,
    onClose,
    layout: 'center',
    skipHighlightOnLastStep: true,
  });

  if (!isOpen || !tour.currentStepData) return null;

  return (
    <GuidedTourOverlay
      isOpen={isOpen}
      tourTitle="Экскурсия по AI Tuner"
      stepsCount={tour.stepCount}
      currentStep={tour.safeStepIndex}
      stepTitle={tour.currentStepData.title}
      stepDescription={tour.currentStepData.description}
      tooltipX={tour.tooltipPosition.x}
      tooltipY={tour.tooltipPosition.y}
      position={tour.currentPosition}
      dataStep={tour.safeStepIndex === tour.stepCount - 1 ? 'sidebar' : undefined}
      onClose={tour.handleSkip}
      onNext={tour.handleNext}
      onPrev={tour.handlePrev}
    />
  );
};

export default Onboarding;
